import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export function PagoExitoPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [tries, setTries] = useState(0);
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let done = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          done = true;
          setStatus("paid");
          try {
            await refresh();
          } catch {}
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          done = true;
          setStatus("failed");
          return;
        }
      } catch (e) {}
      setTries((t) => t + 1);
    };
    const interval = setInterval(() => {
      if (!done && tries < 15) poll();
      else clearInterval(interval);
    }, 2000);
    poll();
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-10 text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-blue-600 grid place-items-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-bold text-slate-900 notranslate" translate="no">CHRONOVIA</span>
        </Link>

        {status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-blue-600 animate-spin mx-auto" />
            <h1 className="font-display font-bold text-2xl text-slate-900 mt-6">
              Confirmando tu pago...
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              Estamos verificando la transacción con Stripe.
            </p>
          </>
        )}

        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" data-testid="payment-success-icon" />
            <h1 className="font-display font-bold text-2xl text-slate-900 mt-6">
              ¡Suscripción activada!
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              Tu plan ya está activo. Empieza a facturar sin límites.
            </p>
            <Link to="/app">
              <Button
                className="w-full mt-6 bg-blue-600 hover:bg-blue-700"
                data-testid="go-dashboard-btn"
              >
                Ir al panel
              </Button>
            </Link>
          </>
        )}

        {status === "failed" && (
          <>
            <XCircle className="w-14 h-14 text-rose-600 mx-auto" />
            <h1 className="font-display font-bold text-2xl text-slate-900 mt-6">
              El pago no se completó
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              Puedes volver a intentarlo desde la sección de Configuración.
            </p>
            <Link to="/app/configuracion">
              <Button className="w-full mt-6 bg-blue-600 hover:bg-blue-700">
                Volver a intentar
              </Button>
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="w-14 h-14 text-slate-400 mx-auto" />
            <p className="text-sm text-slate-500 mt-4">Sesión no encontrada.</p>
            <Link to="/app">
              <Button variant="outline" className="mt-4">
                Volver
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PagoCanceladoPage() {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-10 text-center">
        <XCircle className="w-14 h-14 text-slate-400 mx-auto" />
        <h1 className="font-display font-bold text-2xl text-slate-900 mt-6">
          Pago cancelado
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          No se ha cobrado nada. Puedes volver a intentarlo cuando quieras.
        </p>
        <Link to="/app/configuracion">
          <Button className="w-full mt-6 bg-blue-600 hover:bg-blue-700">
            Volver a configuración
          </Button>
        </Link>
      </div>
    </div>
  );
}
